import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from '@tanstack/react-form';
import { Bell, Check, Link2, Loader2, AlertTriangle } from 'lucide-react';

import { extractFieldErrors } from '@/shared/utils/errorUtils';
import useChannel from '@/feature/Rules/hooks/useChannel.js';

import BaseModal from '@/shared/components/modals/core/BaseModal';
import Select from '@/shared/components/elements/Select';
import Button from '@/shared/components/elements/Button';

export default function AssignChannelModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) {
  const [serverErrors, setServerErrors] = useState({});

  const { channelList, isLoadingChannels } = useChannel(
    '',
    true,
    1,
    100,
    null,
    null,
    false,
    isOpen,
  );

  const channels = channelList?.items || [];
  const channelOptions = channels.map((ch) => ({
    value: ch.id,
    label: ch.name || `کانال ${ch.id}`,
  }));

  const form = useForm({
    defaultValues: {
      channel_id: '',
    },
    onSubmit: async ({ value }) => {
      try {
        setServerErrors({});
        await onSubmit(value.channel_id);
        onClose();
      } catch (error) {
        setServerErrors(extractFieldErrors(error));
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset();
      setServerErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="افزودن کانال به قانون"
      icon={<Bell size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoading && !isLoadingChannels}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
            className="rounded-xl"
          >
            انصراف
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || isLoadingChannels || channels.length === 0}
            icon={<Check size={18} />}
            iconPosition="left"
            className="rounded-xl"
            onClick={() => form.handleSubmit()}
          >
            اتصال کانال
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-4 p-4 sm:space-y-5 sm:p-6"
      >
        <form.Field name="channel_id">
          {(field) => (
            <Select
              label="انتخاب کانال"
              placeholder={
                isLoadingChannels
                  ? 'در حال دریافت کانال‌ها...'
                  : 'کانال مورد نظر را انتخاب کنید...'
              }
              value={field.state.value}
              onChange={(val) => field.handleChange(val)}
              options={channelOptions}
              error={
                serverErrors.channel_id || field.state.meta.errors?.[0]?.message
              }
              icon={
                isLoadingChannels ? (
                  <Loader2
                    className="text-tertiary-60 animate-spin dark:text-gray-400"
                    size={18}
                  />
                ) : (
                  <Link2 size={18} />
                )
              }
              disabled={isLoading || isLoadingChannels || channels.length === 0}
              required
            />
          )}
        </form.Field>

        {!isLoadingChannels && channels.length === 0 && (
          <div className="mt-2 flex items-start gap-2.5 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-700 transition-colors sm:items-center dark:border-yellow-700/30 dark:bg-yellow-900/20 dark:text-yellow-400">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>
              هیچ کانال فعالی در سیستم یافت نشد. لطفا ابتدا یک کانال اطلاع‌رسانی
              ایجاد کنید.
            </p>
          </div>
        )}
      </form>
    </BaseModal>
  );
}

AssignChannelModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
